// src/utils/contabilizzaPresenze.js
// Logica condivisa tra PresenzePage e ApprovazioneCartelliniPage
// per creare/eliminare movimenti economici all'approvazione/respinta cartellino

import { doc, getDoc, getDocs, setDoc, deleteDoc, collection, query, where, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { numIt } from "./economiaCommessa";
import { segmentiGiornata } from "./segmentiGiornata";
import { usaDiario } from "./diarioOre";

// Stesso calcolo del cartellino (utils/segmentiGiornata.js): un permesso a ore dentro
// l'orario di lavoro non diventa costo di manodopera sulla commessa.
export function calcolaOreGiornoUtil(timbrature) {
  if (!timbrature || timbrature.length === 0) return { segmenti: [] };
  return { segmenti: segmentiGiornata(timbrature).segmenti };
}

export async function contabilizzaApprovazioneCartellino({ orgId, uid, anno, mese, giorni, nomeUtente, docId }) {
  // Segmenti che risultano registrati (una commessa è indicata) ma per cui non si riesce a
  // risalire a un id valido — restano nel cartellino, ma nessun costo li conteggia. Prima
  // venivano scartati in silenzio: ora si segnalano a chi approva, per non far sparire ore
  // reali senza che nessuno se ne accorga.
  const segmentiScartati = [];
  // Leggi impostazioni org
  const orgSnap = await getDoc(doc(db,"organizations",orgId));
  const orgData = orgSnap.exists() ? orgSnap.data() : {};
  const fontOre = orgData.fontOreEconomico || "programmazione";
  if (fontOre !== "presenze") return { ok:true }; // solo fonte presenze crea movimenti

  // Costo orario dipendente
  const costoBase = numIt(orgData.costoOrarioBase) || 0;
  let costoOrario = costoBase;
  let diario = false;
  try {
    const dipSnap = await getDocs(query(collection(db,"dipendenti"),
      where("organizationId","==",orgId), where("nexoraUid","==",uid)));
    if (!dipSnap.empty) {
      const dipData = dipSnap.docs[0].data();
      diario = usaDiario(dipData);
      if (dipData.costoOrario && numIt(dipData.costoOrario)>0)
        costoOrario = numIt(dipData.costoOrario);
    }
  } catch(e) { console.warn("Costo orario dipendente non trovato:", e); }

  // Chi segna le ore sulle commesse nel diario «Le mie ore» (W403): il cartellino resta per
  // presenze e paghe, il costo sulle commesse viene dal diario. Si tolgono eventuali costi
  // del mese già scritti dal cartellino, così le stesse ore non contano due volte.
  if (diario) {
    await eliminaMovimentiCartellino({ docId });
    return { ok:true, diario:true };
  }

  // Nessun movimento economico può nascere a costo zero — le presenze restano tracciate
  // (il cartellino esiste comunque), ma la contabilizzazione economica si blocca finché
  // qualcuno non collega questa persona a un Dipendente con un Costo orario valido, o non
  // imposta un Costo orario base in Impostazioni Organizzazione come ripiego generale.
  if (costoOrario <= 0) {
    return { ok:false, motivo:
      `${nomeUtente} non ha un costo orario collegato — vai in Dipendenti & Collaboratori e collega il suo account con un Costo orario valido, oppure imposta un Costo orario base in Impostazioni Organizzazione. Il cartellino resta in attesa, nessuna ora è stata contabilizzata.` };
  }

  // Dizionario nome→id commesse
  const commesseByName = {};
  try {
    const projSnaps = await getDocs(query(collection(db,"projects"), where("organizationId","==",orgId)));
    projSnaps.docs.forEach(d => {
      const data = d.data();
      if (data.isParent) return; // cartella tecnica, non una commessa reale
      if (data.name) commesseByName[data.name] = d.id;
      if (data.codiceCommessa) commesseByName[data.codiceCommessa] = d.id;
    });
  } catch(e) { console.warn("Impossibile caricare commesse:", e); }

  const prefissoMov = `pres_${orgId}_${uid}_${anno}_${mese}`;

  for (const [giornoStr, g] of Object.entries(giorni||{})) {
    const timb = g?.timbrature || [];
    const r = calcolaOreGiornoUtil(timb);
    const orePerCommessa = {};
    const segmentiNormali = r.segmenti.filter(s=>!s.speciale);

    // Totale minuti lordi della giornata — per calcolare la pausa proporzionale
    const totMinutiGiorno = segmentiNormali.reduce((s, seg) => s + seg.minuti, 0);
    // Pausa pranzo: 60 minuti se giornata >= 6h (360 minuti), come nel cartellino
    const pausaGiorno = totMinutiGiorno >= 360 ? 60 : 0;

    segmentiNormali.forEach(s => {
      const chiave = s.commessaNome || "";
      if (!chiave) return;
      const cId = s.commessaId || commesseByName[chiave] || "";
      if (!cId) { segmentiScartati.push(`${giornoStr}: "${chiave}"`); return; }
      if (!orePerCommessa[cId]) orePerCommessa[cId] = { minuti:0, nome:chiave };
      // Detrai pausa proporzionalmente al peso del segmento sul totale giornata
      const pausaProporzionale = totMinutiGiorno > 0
        ? Math.round((s.minuti / totMinutiGiorno) * pausaGiorno)
        : 0;
      orePerCommessa[cId].minuti += Math.max(0, s.minuti - pausaProporzionale);
    });

    for (const [cId, dati] of Object.entries(orePerCommessa)) {
      // Stesso principio della sincronizzazione in tempo reale: il toggle "Economico ON/OFF"
      // nasconde solo la scheda nell'interfaccia, non deve bloccare la registrazione del dato.
      const oreReali = dati.minuti / 60;
      const importo = Math.round(oreReali * costoOrario * 100) / 100;
      const movId = `${prefissoMov}_${giornoStr}_${cId}`;
      await setDoc(doc(db,"movimentiEconomici",movId), {
        projectId: cId, organizationId: orgId,
        tipo: "costo", categoriaId: "manodopera", categoriaLabel: "Manodopera",
        importo, ore: oreReali,
        dati: { descrizione:`Presenze — ${giornoStr}/${mese}/${anno}`, dipendente: nomeUtente, giorno: giornoStr },
        presenzeRef: docId, createdAt: serverTimestamp(), modificataAt: serverTimestamp()
      });
    }
  }
  return { ok:true, segmentiScartati };
}

export async function eliminaMovimentiCartellino({ docId }) {
  try {
    const orgId = docId.split("_")[0];
    const movSnap = await getDocs(query(collection(db,"movimentiEconomici"), where("presenzeRef","==",docId), where("organizationId","==",orgId)));
    for (const d of movSnap.docs) await deleteDoc(d.ref);
  } catch(e) { console.warn("Errore eliminazione movimenti:", e); }
}
